import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  Grid,
  Switch,
  Typography
} from '@mui/material';
import { AlertColor } from '@mui/material/Alert';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { usePutAutoPilotSettings } from 'modules/Settings/hooks/usePutAutoPilotSettings';
import { useAutoPilotContext } from './AutoPilotContextProvider';

const useStyles = makeStyles((theme) => ({
  content: {
    paddingBottom: theme.spacing(4)
  },
  actions: {
    paddingBottom: theme.spacing(4),
    paddingRight: theme.spacing(4)
  },
  skeleton: {
    minWidth: theme.spacing(45)
  },
  div: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column'
  },
  description: {
    color: theme.palette.text.secondary
  }
}));

interface AutoPilotSettingsProps {
  setSeverity: React.Dispatch<React.SetStateAction<AlertColor>>;
  setAlert: React.Dispatch<React.SetStateAction<boolean>>;
  onClose: () => void;
}

const AutoPilotSettings: React.FC<AutoPilotSettingsProps> = ({
  setSeverity,
  setAlert,
  onClose
}: AutoPilotSettingsProps) => {
  const classes = useStyles();
  const { putAutoPilotSettings } = usePutAutoPilotSettings();
  const { setAutoPilotEnabled, isAutoPilotEnabled } = useAutoPilotContext();

  const initialValue = useMemo(() => isAutoPilotEnabled, [isAutoPilotEnabled]);

  const [autoPilot, setAutoPilot] = useState(initialValue);

  const handleToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setAutoPilot(event.target.checked);
    },
    []
  );

  const handleSave = useCallback(async () => {
    onClose();
    try {
      await putAutoPilotSettings({ enabled: autoPilot });
      setAutoPilotEnabled(autoPilot);
    } catch {
      setSeverity('error');
    }
    setAlert(true);
  }, [
    autoPilot,
    onClose,
    putAutoPilotSettings,
    setAlert,
    setAutoPilotEnabled,
    setSeverity
  ]);

  const isDisabled = useMemo(() => initialValue === autoPilot, [
    autoPilot,
    initialValue
  ]);

  return (
    <div className={classes.div}>
      <DialogContent
        className={classes.content}
        data-testid="auto-pilot-content"
      >
        <Grid container>
          <Grid item xs={11}>
            <Typography variant="body1">
              <FormattedMessage id="settingsDialog.autoPilot" />
            </Typography>
            <Typography variant="body2" className={classes.description}>
              <FormattedMessage id="settingsDialog.description" />
            </Typography>
          </Grid>
          <Grid item xs={1}>
            <Switch
              checked={autoPilot}
              data-testid="settings-auto-select-toggle"
              onChange={handleToggle}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          variant="text"
          onClick={onClose}
          data-testid="settings-auto-select-cancel-button"
        >
          <Typography color="textSecondary" fontWeight={500}>
            <FormattedMessage id="settingsDialog.cancel" />
          </Typography>
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSave}
          data-testid="settings-auto-select-save-button"
          disabled={isDisabled}
        >
          <FormattedMessage id="settingsDialog.save" />
        </Button>
      </DialogActions>
    </div>
  );
};

export default React.memo(AutoPilotSettings);
